/**
 * Anonymous-tap claim token: a long-lived opaque cookie that lets us bind
 * IYKTap rows created before a user signed up to that user once they do.
 *
 * Written on /api/iyk/verify. Read on signup and sign-in to retroactively
 * attach any matching anonymous taps to the new/existing user.
 */

import { randomUUID } from 'crypto';
import type { NextResponse } from 'next/server';

export const CLAIM_TOKEN_COOKIE = 'sh_claim_token';

/** sessionStorage key for passing the attach result across a post-auth redirect. */
export const ATTACHED_TAPS_STORAGE_KEY = 'sh_attached_taps';

const DEFAULT_TTL_DAYS = 180;
const CAMPAIGN_GRACE_DAYS = 30;

const DAY_MS = 86_400_000;

export function generateClaimToken(): string {
  return randomUUID();
}

/**
 * Cookie max-age in seconds. Extends to campaign end + 30 days if that's
 * further out than the default 180-day rolling window, so a tap during a
 * long-running campaign stays attachable through its final days.
 */
export function computeClaimTokenMaxAge(campaignEndAt: Date | null | undefined): number {
  const now = Date.now();
  const defaultExpiry = now + DEFAULT_TTL_DAYS * DAY_MS;
  const campaignExpiry = campaignEndAt
    ? campaignEndAt.getTime() + CAMPAIGN_GRACE_DAYS * DAY_MS
    : 0;
  const target = Math.max(defaultExpiry, campaignExpiry);
  return Math.floor((target - now) / 1000);
}

export function setClaimTokenCookie(
  response: NextResponse,
  token: string,
  campaignEndAt: Date | null | undefined
): void {
  response.cookies.set(CLAIM_TOKEN_COOKIE, token, {
    httpOnly: true,
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
    path: '/',
    maxAge: computeClaimTokenMaxAge(campaignEndAt),
  });
}
